import $ from "jquery";
import { TargetDate } from "./target_date";

// Interface definitions
interface InvalidCombination {
  type?: string;
  partition?: string;
  hasDimension?: boolean;
}

interface TabData {
  active: number;
  activate: (event: any, ui: any) => void;
}

class ConditionPanel {
  private formElement: JQuery;
  private typeButtonGroup: TypeButtonGroup;
  private partitionButtonGroup: PartitionButtonGroup;
  public targetDate: any; // tablemill.TargetDate
  private drilldownGroup: DrilldownGroup;

  constructor(
    formElement: JQuery,
    isCombinationComparisonsView: boolean = false,
  ) {
    this.formElement = formElement;
    this.typeButtonGroup = new TypeButtonGroup();
    this.partitionButtonGroup = new PartitionButtonGroup();
    this.targetDate = new TargetDate(this.targetElement());

    if (isCombinationComparisonsView) {
      this.drilldownGroup = new CombinationComparisonsDrilldownGroup();
    } else {
      this.drilldownGroup = new DrilldownGroup();
    }
  }

  // Element getters
  private typeElement(): JQuery {
    return $("#type");
  }

  private partitionElement(): JQuery {
    return $("#partition");
  }

  private targetElement(): JQuery {
    return $("#target");
  }

  private drilldownClearButtonElement(): JQuery {
    return $("#drilldown_clear_button");
  }

  private areaElement(): JQuery {
    return $("#area");
  }

  private categoryElement(): JQuery {
    return $("#category");
  }

  private dimensionElement(): JQuery {
    return $("#dimension");
  }

  // Getters
  getType(): string {
    return this.typeElement().val() as string;
  }

  getPartition(): string {
    return this.partitionElement().val() as string;
  }

  getDimension(): string {
    return this.dimensionElement().val() as string;
  }

  getArea(): string {
    return this.drilldownGroup.getArea();
  }

  getCategory(): string {
    return this.drilldownGroup.getCategory();
  }

  initialize(invalidCombinationSet: InvalidCombination[]): void {
    this.typeButtonGroup.initialize(this.getType());
    this.partitionButtonGroup.initialize(this.getPartition());
    this.drilldownGroup.initialize();
    this.targetDate.initialize();
    this.initializeEventHandlers();
    this.disableInvalidCombination(invalidCombinationSet);
  }

  private initializeEventHandlers(): void {
    this.targetDate.onChange((_date: any) => {
      this.submit();
    });

    this.typeButtonGroup.onItemClick((type: string) => {
      if (!this.typeButtonGroup.isTheSameSelection(type)) {
        this.typeButtonGroup.flipSelectedButton(type);
        this.submit();
      }
    });

    this.partitionButtonGroup.onItemClick((partition: string) => {
      if (!this.partitionButtonGroup.isTheSameSelection(partition)) {
        this.partitionButtonGroup.flipSelectedButton(partition);
        this.submit();
      }
    });

    this.drilldownGroup.areaTabButtons.onItemClick((area: string) => {
      if (this.drilldownGroup.areaTabButtons.isTheSameSelection(area)) {
        this.drilldownGroup.areaTabButtons.removeSelectedButton();
      } else {
        this.drilldownGroup.areaTabButtons.flipSelectedButton(area);
        this.drilldownGroup.categoryTabButtons.removeSelectedButton();
      }
      this.submit();
    });

    this.drilldownGroup.categoryTabButtons.onItemClick((category: string) => {
      if (this.drilldownGroup.categoryTabButtons.isTheSameSelection(category)) {
        this.drilldownGroup.categoryTabButtons.removeSelectedButton();
      } else {
        this.drilldownGroup.categoryTabButtons.flipSelectedButton(category);
        this.drilldownGroup.areaTabButtons.removeSelectedButton();
      }
      this.submit();
    });

    this.drilldownClearButtonElement().on("click", (_e) => {
      this.drilldownGroup.removeSelectedButton();
      this.submit();
    });
  }

  public anyDimensionHasSelected(): boolean {
    if (this.drilldownGroup.areaTabButtons.hasSelectedButton()) return true;
    if (this.drilldownGroup.categoryTabButtons.hasSelectedButton()) return true;
    return false;
  }

  private disableInvalidCombination(
    invalidCombinationSet: InvalidCombination[],
  ): void {
    const currentType = this.getType();
    const currentPartition = this.getPartition();

    invalidCombinationSet.forEach((combi) => {
      const type = combi.type || null;
      const partition = combi.partition || null;
      const hasDimension = combi.hasDimension || null;

      if (type && partition) {
        if (currentType === type) {
          if (currentPartition !== partition) {
            this.partitionButtonGroup.disableButton(partition);
          }
        } else {
          if (currentPartition === partition) {
            this.typeButtonGroup.disableButton(type);
          }
        }
      }

      if (partition && hasDimension) {
        if (hasDimension && this.anyDimensionHasSelected()) {
          this.partitionButtonGroup.disableButton(partition);
        }
        if (currentPartition === partition) {
          this.drilldownGroup.disableDrilldownButton();
        }
      }
    });
  }

  private submit(): void {
    this.formElement.submit();
  }
}

// Base ButtonGroup class
abstract class ButtonGroup {
  protected buttonElements: JQuery<HTMLElement> | JQuery<HTMLLabelElement>;
  protected selector: string;

  constructor() {
    this.buttonElements = $() as JQuery<HTMLElement>;
    this.selector = "";
  }

  abstract getSelector(): string;
  abstract getButtonElements(): JQuery<HTMLElement> | JQuery<HTMLLabelElement>;

  initialize(selected: string): void {
    this.selectSelectedButton(selected);
  }

  protected selectSelectedButton(selected: string): void {
    const elem = this.getButtonElements()
      .toArray()
      .find((elem) => {
        const dataValue = $(elem).data(this.getSelector());
        return dataValue !== undefined && dataValue !== null && (
          dataValue.toString() === selected.toString()
        );
      });
    if (elem) {
      $(elem).addClass("selected");
    }
  }

  selectedButton(): HTMLElement | null {
    const selectedElements = this.getButtonElements()
      .toArray()
      .filter((elem) => {
        return $(elem).hasClass("selected");
      }) as HTMLElement[];
    return selectedElements.length > 0 ? selectedElements[0] : null;
  }

  flipSelectedButton(newId: string, parameterElement?: JQuery): void {
    const selected = this.selectedButton();
    if (selected) {
      $(selected).removeClass("selected");
    }
    this.selectSelectedButton(newId);
    if (parameterElement) {
      parameterElement.val(newId);
    }
  }

  onItemClick(f: (value: string) => void): void {
    const selector = this.getSelector();
    this.getButtonElements().on("click", function (evt) {
      const button = $(evt.currentTarget).data(selector);
      f(button);
    });
  }

  getNotation(index: number): string {
    const elements = this.getButtonElements().toArray();
    if (elements[index]) {
      return $(elements[index]).data("notation") || "";
    }
    return "";
  }

  isTheSameSelection(selection: string): boolean {
    const selectedButton = this.selectedButton();
    if (selectedButton) {
      return $(selectedButton).data(this.getSelector()) === selection;
    }
    return false;
  }

  hasSelectedButton(): boolean {
    return this.getButtonElements().hasClass("selected");
  }

  disableButton(id: string): void {
    const selector = this.getSelector();
    const targetButton = this.getButtonElements()
      .toArray()
      .find((button) => {
        return $(button).data(selector) === id;
      });
    if (targetButton) {
      $(targetButton).off("click");
      $(targetButton).addClass("disabled");
    }
  }
}

class TypeButtonGroup extends ButtonGroup {
  constructor() {
    super();
    this.buttonElements = $(".type_button");
    this.selector = "type";
  }

  getSelector(): string {
    return this.selector;
  }

  getButtonElements(): JQuery<HTMLElement> | JQuery<HTMLLabelElement> {
    return this.buttonElements;
  }

  flipSelectedButton(selected: string): void {
    super.flipSelectedButton(selected, $("#type"));
  }
}

class PartitionButtonGroup extends ButtonGroup {
  constructor() {
    super();
    this.buttonElements = $("#condition_panel .partition_button");
    this.selector = "partition";
  }

  getSelector(): string {
    return this.selector;
  }

  getButtonElements(): JQuery<HTMLElement> | JQuery<HTMLLabelElement> {
    return this.buttonElements;
  }

  flipSelectedButton(selected: string): void {
    super.flipSelectedButton(selected, $("#partition"));
  }
}

class DrilldownGroup {
  areaTabButtons: AreaTabButtons;
  categoryTabButtons: CategoryTabButtons;
  protected tabSet: TabSet;

  constructor() {
    this.areaTabButtons = new AreaTabButtons();
    this.categoryTabButtons = new CategoryTabButtons();
    this.tabSet = new TabSet();
  }

  private drilldownButtonElement(): JQuery {
    return $("#drilldown_button");
  }

  private areaElement(): JQuery {
    return $("#area");
  }

  private categoryElement(): JQuery {
    return $("#category");
  }

  getArea(): string {
    return this.areaElement().val() as string;
  }

  getCategory(): string {
    return this.categoryElement().val() as string;
  }

  initialize(): void {
    this.areaTabButtons.initialize(this.areaElement().val() as string);
    this.categoryTabButtons.initialize(this.categoryElement().val() as string);
    this.tabSet.initialize();
  }

  removeSelectedButton(): void {
    this.areaTabButtons.removeSelectedButton();
    this.categoryTabButtons.removeSelectedButton();
  }

  disableDrilldownButton(): void {
    this.drilldownButtonElement().addClass("disabled");
    (this.drilldownButtonElement() as any).dropdown("disable");
  }
}

abstract class TabButtonGroup extends ButtonGroup {
  protected drilldownGroupElement(): JQuery {
    return $("#drilldown_group");
  }

  private dimensionElement(): JQuery {
    return $("#dimension");
  }

  protected getDimension(): string {
    return this.dimensionElement().val() as string;
  }

  initialize(selected: string): void {
    super.initialize(selected);
    this.updateDrilldownCondition(selected);
  }

  protected showDrilldownCondition(notation: string): void {
    const conditionNode = `
      <div class="filter_text">
        <span id="drilldown_condition">${notation}</span>
        <label id="drilldown_clear_button" class="icon_font icf_close_circle" />
      </div>
    `;
    this.drilldownGroupElement().append(conditionNode);
  }

  protected hideDrilldownCondition(): void {
    this.drilldownGroupElement().find(".filter_text").hide();
  }

  abstract updateDrilldownCondition(selected: string | null): void;
  abstract removeSelectedButton(): void;
}

class AreaTabButtons extends TabButtonGroup {
  constructor() {
    super();
    this.buttonElements = $("#tab_area.ui_tab_content").find("label");
    this.selector = "area";
  }

  getSelector(): string {
    return this.selector;
  }

  getButtonElements(): JQuery<HTMLElement> | JQuery<HTMLLabelElement> {
    return this.buttonElements;
  }

  flipSelectedButton(selected: string): void {
    super.flipSelectedButton(selected, $("#area"));
    this.updateDrilldownCondition(selected);
  }

  removeSelectedButton(): void {
    this.buttonElements.removeClass("selected");
    $("#area").val("");
    this.updateDrilldownCondition(null);
  }

  updateDrilldownCondition(selected: string | null): void {
    if (this.getDimension() !== this.selector) return;

    if (selected) {
      const notation = this.drilldownConditionNotation(
        parseInt(selected, 10) - 1,
      );
      this.showDrilldownCondition(notation);
    } else {
      this.hideDrilldownCondition();
    }
  }

  protected drilldownConditionNotation(index: number): string {
    return `分析軸：地域（${this.getNotation(index)}）`;
  }
}

class CategoryTabButtons extends TabButtonGroup {
  protected static readonly MALE_ID = "101";
  protected static readonly MALE_INDEX = 9;

  constructor() {
    super();
    this.buttonElements = $("#tab_category.ui_tab_content").find("label");
    this.selector = "category";
  }

  getSelector(): string {
    return this.selector;
  }

  getButtonElements(): JQuery<HTMLElement> | JQuery<HTMLLabelElement> {
    return this.buttonElements;
  }

  flipSelectedButton(selected: string): void {
    super.flipSelectedButton(selected, $("#category"));
    this.updateDrilldownCondition(selected);
  }

  removeSelectedButton(): void {
    this.buttonElements.removeClass("selected");
    $("#category").val("");
    this.updateDrilldownCondition(null);
  }

  updateDrilldownCondition(selected: string | null): void {
    if (this.getDimension() !== this.selector) return;

    if (selected) {
      const index =
        selected.toString() === CategoryTabButtons.MALE_ID
          ? CategoryTabButtons.MALE_INDEX
          : parseInt(selected, 10) - 1;
      const notation = this.drilldownConditionNotation(index);
      this.showDrilldownCondition(notation);
    } else {
      this.hideDrilldownCondition();
    }
  }

  protected drilldownConditionNotation(index: number): string {
    return `分析軸：年代・性別（${this.getNotation(index)}）`;
  }
}

class CombinationComparisonsDrilldownGroup extends DrilldownGroup {
  constructor() {
    super();
    this.areaTabButtons = new CombinationComparisonsAreaTabButtons();
    this.categoryTabButtons = new CombinationComparisonsCategoryTabButtons();
    this.tabSet = new TabSet();
  }
}

class CombinationComparisonsAreaTabButtons extends AreaTabButtons {
  protected showDrilldownCondition(notation: string): void {
    const conditionNode = `
      <div class="combi_comp_dimension_text">
        <span>${notation}</span>
      </div>
    `;
    this.drilldownGroupElement().append(conditionNode);
  }

  protected drilldownConditionNotation(index: number): string {
    return `全国 と <b><u>${this.getNotation(index)}</u></b> を比較`;
  }

  updateDrilldownCondition(selected: string | null): void {
    if (this.getDimension() !== this.selector) return;

    if (selected) {
      const notation = this.drilldownConditionNotation(
        parseInt(selected, 10) - 1,
      );
      this.showDrilldownCondition(notation);
    } else {
      this.hideDrilldownCondition();
    }
  }
}

class CombinationComparisonsCategoryTabButtons extends CategoryTabButtons {
  protected showDrilldownCondition(notation: string): void {
    const conditionNode = `
      <div class="combi_comp_dimension_text">
        <span>${notation}</span>
      </div>
    `;
    this.drilldownGroupElement().append(conditionNode);
  }

  protected drilldownConditionNotation(index: number): string {
    return `全体 と <b><u>${this.getNotation(index)}</u></b> を比較`;
  }

  updateDrilldownCondition(selected: string | null): void {
    if (this.getDimension() !== this.selector) return;

    if (selected) {
      const index =
        selected.toString() === CategoryTabButtons.MALE_ID
          ? CategoryTabButtons.MALE_INDEX
          : parseInt(selected, 10) - 1;
      const notation = this.drilldownConditionNotation(index);
      this.showDrilldownCondition(notation);
    } else {
      this.hideDrilldownCondition();
    }
  }
}

class TabSet {
  private selected: Tab | null;
  private tabs: Tab[];
  private tabPositions: Record<string, number>;

  constructor() {
    this.selected = null;
    this.tabs = [new Tab("area"), new Tab("category")];
    this.tabPositions = {};
    this.tabs.forEach((tab, i) => {
      this.tabPositions[tab.key()] = i;
    });
  }

  private tabRootElement(): JQuery {
    return $("#tabs");
  }

  private dimensionElement(): JQuery {
    return $("#dimension");
  }

  private getDimension(): string {
    return this.dimensionElement().val() as string;
  }

  initialize(): void {
    const tabData: TabData = {
      active: this.tabPositions[this.getDimension()],
      activate: (event: any, ui: any) => {
        this.selected = this.lookUp(ui.newPanel.index() - 1);
        this.dimensionElement().val(this.selected.key());
      },
    };

    (this.tabRootElement() as any).tabs(tabData);
  }

  selectedTab(): Tab | null {
    return this.selected;
  }

  private lookUp(pos: number): Tab {
    return this.tabs[pos];
  }
}

class Tab {
  private tab: string;

  constructor(tab: string) {
    this.tab = tab;
  }

  key(): string {
    return this.tab;
  }
}

export { ConditionPanel };
